// PlanComponents/RecruitsPeriodCard.js
import React from 'react';
import { Typography, Box, LinearProgress, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTheme } from '@mui/material/styles';
import { bg, text as textColors, border } from '../../../utils/darkModeAdapter';

import CardItem from './CardItem';
import AdviceContentBox from '../../../components/AdviceContentBox';
import { calcDays, calcMonths } from './utils';

// ベンチマーク（Gem 2025 Recruiting Benchmarks）
const AVG_TIME_TO_HIRE = 41; // グローバル平均41日

// 業界別参考値（Corporate Navigators 2025）
const INDUSTRY_BENCHMARKS = [
  { label: '製造業', days: 30.7 },
  { label: 'IT', days: 33.0 },
  { label: '金融', days: 44.7 },
  { label: 'シニア', days: 90 },
];

/**
 * 採用期間の目安
 * 科学的根拠: Gem 2025 Recruiting Benchmarks, Corporate Navigators 2025
 */
export default function RecruitsPeriodCard({ recruitsPeriod, adv, startDate, endDate, hiringMetrics }) {
  const theme = useTheme();

  const days = calcDays(startDate, endDate);
  const months = calcMonths(startDate, endDate);
  const estimatedDays = hiringMetrics?.estimatedDaysToHire || null;

  // 期間 vs ベンチマーク比較のパーセント（最大200%）
  const comparePercent = days > 0 ? Math.min(200, Math.round((days / AVG_TIME_TO_HIRE) * 100)) : 0;

  return (
    <CardItem
      title="採用期間の目安"
      adviceContent={<AdviceContentBox adviceObj={adv.recruitsPeriod} />}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* AI生成テキスト */}
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {recruitsPeriod || '未設定'}
        </Typography>

        {/* 期間計算結果 */}
        {days > 0 && (
          <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: bg(theme).subtle, border: `1px solid ${border(theme).light}` }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: textColors(theme).primary, mb: 1 }}>
              採用期間: {days}日間（約{months}ヶ月）
            </Typography>

            {/* グローバル平均との比較バー */}
            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography variant="caption" sx={{ color: textColors(theme).muted }}>
                  グローバル平均: {AVG_TIME_TO_HIRE}日
                </Typography>
                <Typography variant="caption" sx={{ color: textColors(theme).muted }}>
                  {comparePercent}%
                </Typography>
              </Box>
              <Box sx={{ position: 'relative' }}>
                <LinearProgress
                  variant="determinate"
                  value={Math.min(100, comparePercent)}
                  sx={{
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: 'rgba(0,0,0,0.06)',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: comparePercent <= 100 ? '#4caf50' : comparePercent <= 150 ? '#ff9800' : '#f44336',
                      borderRadius: 4
                    }
                  }}
                />
                <Box sx={{
                  position: 'absolute',
                  left: '50%',
                  top: -1,
                  width: 2,
                  height: 10,
                  backgroundColor: textColors(theme).muted,
                  opacity: 0.5
                }} />
              </Box>
            </Box>

            {/* 予測採用日数（バックエンド計算値） */}
            {estimatedDays && (
              <Typography variant="caption" sx={{ color: textColors(theme).muted }}>
                AI予測: 1名あたり約{estimatedDays}日
                {hiringMetrics?.estimatedDaysRange && (
                  <span> ({hiringMetrics.estimatedDaysRange.min}〜{hiringMetrics.estimatedDaysRange.max}日)</span>
                )}
              </Typography>
            )}
          </Box>
        )}

        {/* 業界別参考値 */}
        {days > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            <Tooltip
              title="業界別Time to Fill: 製造30.7日 / IT33.0日 / 金融44.7日 / シニア90日超"
              arrow
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3, cursor: 'help' }}>
                <InfoOutlinedIcon sx={{ fontSize: 14, color: textColors(theme).muted }} />
                <Typography variant="caption" sx={{ color: textColors(theme).muted }}>
                  業界別参考値:
                </Typography>
              </Box>
            </Tooltip>
            {INDUSTRY_BENCHMARKS.map(b => (
              <Typography key={b.label} variant="caption" sx={{ color: textColors(theme).muted, mr: 1 }}>
                {b.label} {b.days}日
              </Typography>
            ))}
          </Box>
        )}

        {/* 出典 */}
        {days > 0 && (
          <Typography variant="caption" sx={{ color: textColors(theme).muted, fontStyle: 'italic' }}>
            ※ 採用期間の平均値は業界調査に基づく
          </Typography>
        )}
      </Box>
    </CardItem>
  );
}
